import React from "react";
import { DraftItemProps, SeedType } from "../../utils/common";
import { Card, CardHeader, CardBody } from "@material-tailwind/react";
import Avatar from "../../design-system/Avatar";
import styles from "./Draft.module.css";
import ReactQuill from "react-quill";
import { useNavigate } from "react-router-dom";
import { FaLock } from "react-icons/fa6";
import { FaCheckCircle } from "react-icons/fa";
import { MdDelete, MdDeleteForever } from "react-icons/md";
import { Tooltip } from "@material-tailwind/react";
import { CiCircleCheck } from "react-icons/ci";

function DraftItem({ draft, onDelete }: DraftItemProps & { onDelete: (fileName: string) => void }) {
    const deleteDraftClick = (fileName: string) => (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation(); // Prevent navigation when clicking delete
        onDelete(fileName);
    };
    const navigate = useNavigate();
    function getNumberOfDaysSinceTimestamp(timestamp: string | number): string {
        const inputDate = new Date(timestamp);
        const currentDate = new Date();
        inputDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);
        currentDate.setDate(currentDate.getDate());

        const differenceInTime = currentDate.getTime() - inputDate.getTime();
        const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));
        let result = "";
        if (differenceInDays === 1 || differenceInDays === 0) {
            result += `${differenceInDays} day`;
        } else {
            result += `${differenceInDays} days`;
        }

        return result;
    }

    const getInitials = (name: string) => {
        return name
            ?.split(" ")
            .map(n => n[0])
            .join("");
    };
    const openSpecificDraftClick = (fileName: String) => (event: React.MouseEvent<HTMLDivElement>) => {
        if (draft.draft) navigate(`/my-exclusive-content/draft/${fileName}`, { state: { fileName: fileName } });
        else navigate(`/my-exclusive-content/published/${fileName}`, { state: { fileName: fileName } });
    };
    function capitalizeFirstLetter(str:string ) {
        if (str.length === 0) {
          return ""; // Handle empty strings
        }
        return str.charAt(0).toUpperCase() + str.slice(1);
      }

    const initials = getInitials(draft.author);
    return (
        <Card
            onClick={draft.file_name ? openSpecificDraftClick(draft.file_name) : openSpecificDraftClick(draft.id)}
            className="mt-4 cursor-pointer hover:cursor-pointer min-w-96"
        >
            <div className={`${styles.header} p-1 text-gray-700`}>
                <div className="flex items-center">
                    <Tooltip content={draft.author} className="bg-neutral-600 w-fit h-7 text-center p-1  top-full left-0 z-50" placement="right">
                        <div>
                            <Avatar initials={initials} className="mr-2" />
                        </div>
                    </Tooltip>
                </div>
                <h2 className={`justify-self-center font-title truncate w-full max-w-full`}>{draft.title}</h2>
                {draft?.status_info && (draft?.status_info?.status === 'pending' || draft?.status_info?.status === 'failed') ? (
                    <div className="font-medium">{capitalizeFirstLetter(draft?.status_info?.status || "")}</div>
                ) : (
                    <div className=""></div>
                )}
                <Tooltip content="Exclusive Content" className="bg-neutral-600 w-fit h-7 text-center p-1 absolute top-full left-0 z-50" placement="top">
                    <div className=" justify-self-center flex gap-6">
                        <FaLock size={18} />
                    </div>
                </Tooltip>

                <div>
                    {draft.draft ? (
                        <Tooltip content="Draft Content" className="bg-neutral-600 w-fit h-7 text-center p-1 absolute top-full left-0 z-50" placement="top">
                            <div className="justify-self-center">
                                <CiCircleCheck size={18} />{" "}
                            </div>
                        </Tooltip>
                    ) : (
                        <Tooltip content="Published Content" className="bg-neutral-600 w-fit h-7 text-center p-1 absolute top-full left-0 z-50" placement="top">
                            <div className="justify-self-center">
                                <FaCheckCircle size={18} />{" "}
                            </div>
                        </Tooltip>
                    )}
                </div>

                <div className="w-6 h-6 justify-self-center flex rounded-full gap-6">
                    {!draft.draft ? (
                        ""
                    ) : (
                        <Tooltip content="Delete Content" className="bg-neutral-600 w-fit h-7 text-center p-1 absolute top-full left-0 z-50" placement="top">
                            <div className="">
                                <MdDeleteForever
                                    className="size-6 text-red-800"
                                    onClick={(event: any) => {
                                        event.stopPropagation(); // Prevents the click from propagating to the parent (Card)
                                        draft.file_name ? deleteDraftClick(draft.file_name)(event) : deleteDraftClick(draft.id)(event);
                                    }}
                                />
                            </div>
                        </Tooltip>
                    )}
                </div>
            </div>
        </Card>
    );
}

export default DraftItem;
